import { ReactElement } from "react";
import { JSONEditorProps, PopoverProps, RangeValue } from "@evergis/ui";
import {
  PostgresLayerServiceConfigurationDc,
  PostgresTileCatalogServiceConfigurationDc,
  ProxyServiceConfigurationDc,
  QueryLayerServiceConfigurationDc,
  RemoteTileServiceConfigurationDc,
} from "@evergis/api";

import { LayerInfo } from "../../interfaces/resourceInfo";

export enum LayerConfigurationTab {
  BaseParameters = "BaseParameters",
  AttributeParameters = "AttributeParameters",
  Eql = "Eql",
  EqlParameters = "EqlParameters",
  Attributes = "Attributes",
  Style = "Style",
  Acl = "Acl",
  ClientData = "ClientData",
  FeatureCard = "FeatureCard",
  RelatedDataSources = "RelatedDataSources",
  Simplify = "Simplify",
}

export enum ProjectConfigurationTab {
  Resource = "Resource",
  Map = "Map",
  Dashboard = "Dashboard",
  Data = "Data",
}

export type ConfigurationsTab = {
  name: string;
  title?: string;
  mode?: JSONEditorProps["options"]["mode"];
  theme?: string;
  disabled?: boolean;
};

export type ConfigurationsHeaderProps = {
  title: ReactElement;
  toolbar?: ReactElement;
  buttons?: ReactElement;
  info?: ReactElement;
  reference: ReactElement;
  tabs: ConfigurationsTab[];
  activeTab: ConfigurationsTab;
  setActiveTab: (tab: ConfigurationsTab) => void;
  getValue: GetConfigValue;
  onSuccess?: VoidFunction;
};

export type EqlParametersProps = {
  items: EqlParameterItems;
  onSave: SaveEqlParameter;
  onPaste?: SaveEqlParameter;
  popoverProps?: Partial<PopoverProps>;
};

export type RemoteTileServiceConfiguration = RemoteTileServiceConfigurationDc & {
  resolution?: number;
  defaultScaleRange?: RangeValue;
};

export type LayerInfoWithServiceType = LayerInfo & { type: LayerServiceType };

export type LayerServiceType =
  | "PostgresLayerService"
  | "QueryLayerService"
  | "ProxyService"
  | "RemoteTileService"
  | "PostgresTileLayerService";

export type LayerConfigurationDc =
  | PostgresLayerServiceConfigurationDc
  | QueryLayerServiceConfigurationDc
  | ProxyServiceConfigurationDc
  | RemoteTileServiceConfigurationDc
  | PostgresTileCatalogServiceConfigurationDc;

export type ConfigurationType<T extends LayerServiceType = "PostgresLayerService"> = T extends "QueryLayerService"
  ? QueryLayerServiceConfigurationDc
  : T extends "ProxyService"
  ? ProxyServiceConfigurationDc
  : T extends "RemoteTileService"
  ? RemoteTileServiceConfigurationDc
  : T extends "PostgresTileLayerService"
  ? PostgresTileCatalogServiceConfigurationDc
  : PostgresLayerServiceConfigurationDc;

export type GetConfigValue = () => ConfigurationType;

export type SaveEqlParameter = (value: unknown) => void;

export type EqlParameterValue = {
  alias: string;
  availableValues: string[];
  default: string;
  description: string;
  descriptionColumn: string;
  query: string;
  type: string;
  valueColumn: string;
};

export type EqlParameterPopoverProps = {
  eqlParameters: EqlParameterItems;
  paramKey?: string;
  paramValue?: EqlParameterValue;
  onSave: SaveEqlParameter;
  onClose: VoidFunction;
};

export type EqlParameterItems = Record<string, EqlParameterValue>;

export enum PageMenuOption {
  Layer = "Layer",
  DataSourceLayer = "DataSourceLayer",
  DataSourceQuery = "DataSourceQuery",
  Filter = "Filter",
  Container = "Container",
}
